// src\components\Payroll\ProcessPartialSalaryFld\shared\useProcessPartialSalary.js
import { useState, useCallback, useEffect } from "react";
import axios from '../../../../api/apiClient';
import { domain, token } from '../../../Security';

export const useProcessPartialSalary = ({ user, showGeneralMessage }) => {
  const [loadingProcessed, setLoadingProcessed] = useState(false);
  const [loadingPosted, setLoadingPosted] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);
  const [loadingPercentage, setLoadingPercentage] = useState(0);
  const [loadingSpinner, setLoadingSpinner] = useState(false); // ADDED: for non-progress-bar actions

  const [batches, setBatches] = useState([]);
  const [processedData, setProcessedData] = useState([]);
  const [postedData, setPostedData] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showDtrModal, setShowDtrModal] = useState(false);

  const [showEditForm, setShowEditForm] = useState(false);
  const [selectedEditRow, setSelectedEditRow] = useState(null);

  const [filterBatchId, setFilterBatchId] = useState("");
  const [isGenerate, setIsGenerate] = useState(false);

  // ── Simulate loading bar — mirrors payroll.js pattern ─────────────────────
  const simulateLoading = (targetPercentage, current, setter, intervalTime = 200) => {
    return new Promise((resolve) => {
      let progress = current;
      const interval = setInterval(() => {
        if (progress < targetPercentage) {
          progress += 1;
          setter(progress);
        } else {
          clearInterval(interval);
          resolve(progress);
        }
      }, intervalTime);
    });
  };

  // ── Fetch all batches for the dropdown ────────────────────────────────────
  // MODIFIED: added loadingSpinner
  const fetchBatches = useCallback(async () => {
    try {
      setLoadingSpinner(true);
      const response = await axios.get(`${domain}/api/PartialSalaryBatch`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBatches(response.data);
    } catch (error) {
      console.error("Error fetching batches:", error);
    } finally {
      setLoadingSpinner(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  // ── Fetch processed (EmployeePartialSalaryStaging) — Tab 1 ────────────────
  // MODIFIED: added loadingSpinner alongside loadingProcessed
  const fetchProcessedData = useCallback(async () => {
    if (!filterBatchId) return;
    try {
      setLoadingProcessed(true);
      setLoadingSpinner(true);
      const response = await axios.get(
        `${domain}/api/PartialSalaryBatch/staging-employees/${filterBatchId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProcessedData(response.data);
    } catch (error) {
      console.error("Error fetching processed data:", error);
      setProcessedData([]);
    } finally {
      setLoadingProcessed(false);
      setLoadingSpinner(false);
    }
  }, [filterBatchId]);

  // ── Fetch posted (EmployeePartialSalary) — Tab 2 ──────────────────────────
  // MODIFIED: added loadingSpinner alongside loadingPosted
  const fetchPostedData = useCallback(async () => {
    if (!filterBatchId) return;
    try {
      setLoadingPosted(true);
      setLoadingSpinner(true);
      const response = await axios.get(
        `${domain}/api/PartialSalaryBatch/posted/${filterBatchId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPostedData(response.data);
    } catch (error) {
      console.error("Error fetching posted data:", error);
      setPostedData([]);
    } finally {
      setLoadingPosted(false);
      setLoadingSpinner(false);
    }
  }, [filterBatchId]);

  // ── Generate — fetches both tables ────────────────────────────────────────
  useEffect(() => {
    if (isGenerate) {
      const run = async () => {
        await Promise.all([fetchProcessedData(), fetchPostedData()]);
        setIsGenerate(false);
      };
      run();
    }
  }, [isGenerate, fetchProcessedData, fetchPostedData]);

  // ── Process batch — keeps CustomLoader (requestLoading + loadingPercentage) ─
  const handleProcess = async (batchId, require15DayStreak, onClose) => {
    try {
      setLoadingPercentage(10);
      setRequestLoading(true);
      await simulateLoading(30, 10, setLoadingPercentage, 200);

      const apiUrl = `${domain}/api/PartialSalaryBatch/process/${batchId}?require15DayStreak=${require15DayStreak}`;

      const result = await axios.post(apiUrl, null, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLoadingPercentage(50);
      await simulateLoading(80, 50, setLoadingPercentage, 100);
      await fetchBatches();
      await Promise.all([fetchProcessedData(), fetchPostedData()]);
      await simulateLoading(100, 80, setLoadingPercentage, 100);
      showGeneralMessage(`Batch processed successfully! ${result.data?.qualifiedCount} qualified`, true);
      onClose?.();
    } catch (error) {
      console.error("Process error:", error);
      const msg = error?.response?.data?.message || "Error processing batch.";
      showGeneralMessage(msg, false);
    } finally {
      setTimeout(() => { setLoadingPercentage(0); setRequestLoading(false); }, 500);
    }
  };

  // ── Post batch — keeps CustomLoader ──────────────────────────────────────
  const handlePost = async (batchId, onClose) => {
    try {
      setLoadingPercentage(10);
      setRequestLoading(true);
      await simulateLoading(30, 10, setLoadingPercentage, 200);

      const apiUrl = `${domain}/api/PartialSalaryBatch/post/${batchId}`;

      await axios.post(apiUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setLoadingPercentage(50);
      await simulateLoading(80, 50, setLoadingPercentage, 100);
      await fetchBatches();
      await Promise.all([fetchProcessedData(), fetchPostedData()]);
      await simulateLoading(100, 80, setLoadingPercentage, 100);
      showGeneralMessage("Batch posted successfully!", true);
      onClose?.();
    } catch (error) {
      console.error("Post error:", error);
      const msg = error?.response?.data?.message || "Error posting batch.";
      showGeneralMessage(msg, false);
    } finally {
      setTimeout(() => { setLoadingPercentage(0); setRequestLoading(false); }, 500);
    }
  };

  // ── Revert batch — keeps CustomLoader ────────────────────────────────────
  const handleRevert = async (batchId, onClose) => {
    try {
      setLoadingPercentage(10);
      setRequestLoading(true);
      await simulateLoading(30, 10, setLoadingPercentage, 200);

      await axios.post(
        `${domain}/api/PartialSalaryBatch/revert/${batchId}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLoadingPercentage(50);
      await simulateLoading(80, 50, setLoadingPercentage, 100);
      await fetchBatches();
      await Promise.all([fetchProcessedData(), fetchPostedData()]);
      await simulateLoading(100, 80, setLoadingPercentage, 100);
      showGeneralMessage("Batch reverted successfully!", true);
      onClose?.();
    } catch (error) {
      console.error("Revert error:", error);
      const msg = error?.response?.data?.message || "Error reverting batch.";
      showGeneralMessage(msg, false);
    } finally {
      setTimeout(() => { setLoadingPercentage(0); setRequestLoading(false); }, 500);
    }
  };

  // ── Edit eligibility — uses LoadingSpinner, not CustomLoader ─────────────
  // MODIFIED: no simulateLoading / requestLoading — uses loadingSpinner only
  // ⚠️ Placeholder endpoint — update to match your actual API route
  const handleEditEligibility = async (employeeRow, isEligible, onClose) => {
    try {
      setLoadingSpinner(true);

      await axios.put(
        `${domain}/api/PartialSalaryBatch/staging-employee/${employeeRow.id}/eligibility`,
        { isEligible },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchProcessedData();
      showGeneralMessage("Eligibility updated successfully!", true);
      onClose?.();
    } catch (error) {
      console.error("Edit eligibility error:", error);
      const msg = error?.response?.data?.message || "Error updating eligibility.";
      showGeneralMessage(msg, false);
    } finally {
      setLoadingSpinner(false);
    }
  };

  // ── Open edit eligibility modal ───────────────────────────────────────────
  const openEditForm = useCallback((employeeRow) => {
    setSelectedEditRow(employeeRow);
    setShowEditForm(true);
  }, []);

  // ── Open Employee DTR Modal ────────────────────────────────────────────────
  const openDtrView = useCallback((employeeRow) => {
    console.log({}, employeeRow);
    setSelectedEmployee(employeeRow);
    setShowDtrModal(true);
  }, []);

  return {
    loadingProcessed,
    loadingPosted,
    requestLoading,
    loadingPercentage,
    loadingSpinner, // ADDED
    batches,
    processedData,
    postedData,
    filterBatchId,
    showDtrModal,
    selectedEmployee,
    setShowDtrModal,
    setFilterBatchId,
    setIsGenerate,
    fetchProcessedData,
    fetchPostedData,
    handleProcess,
    handlePost,
    handleRevert,
    openDtrView,
    showEditForm,
    selectedEditRow,
    setShowEditForm,
    openEditForm,
    handleEditEligibility,
  };
};